import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { LoaderIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons';
import { mcpClassApi } from '@/api/mcpClass.api';
import { mcpApi } from '@/api/mcp.api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs } from '@/components/ui/tabs';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { formatTimestamp } from '@/utils/utils';
import { MCP_TRANSPORT_LABELS } from '@/pages/mcp/mcp-constants';
import type { MCPClass } from '@/proto/agentplatform/v1/mcp_pb';
import type { MCP } from '@/proto/agentplatform/v1/mcp_pb';
import type { Param } from '@/proto/agentplatform/v1/param_pb';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

const HEADER_TARGET_PREFIX = 'headers.';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="p-1 hover:bg-accent rounded transition-colors"
      title="复制"
    >
      {copied ? (
        <svg
          className="w-3.5 h-3.5 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

function InfoItem({
  label,
  children,
  copyable,
}: {
  label: string;
  children: React.ReactNode;
  copyable?: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">{children}</div>
        {copyable && <CopyButton text={copyable} />}
      </div>
    </div>
  );
}

type TabId = 'overview' | 'instances';

function OverviewTab({ classData }: { classData: MCPClass }) {
  const tpl = classData.template;
  const headerParamKeys = useMemo(() => {
    return new Set(
      (classData.params || [])
        .filter((p) => p.target?.startsWith(HEADER_TARGET_PREFIX))
        .map((p) => p.target.slice(HEADER_TARGET_PREFIX.length))
    );
  }, [classData.params]);

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="类名称">
              <span className="font-medium">{classData.name}</span>
            </InfoItem>
            <InfoItem label="创建时间">
              <span className="text-sm">{formatTimestamp(classData.createdAt)}</span>
            </InfoItem>
          </div>
          <InfoItem label="描述">
            <span className="text-sm">{classData.description || '暂无描述'}</span>
          </InfoItem>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="MCP 模版 ID" copyable={classData.id}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {classData.id}
              </code>
            </InfoItem>
            {classData.tenantId && (
              <InfoItem label="租户 ID" copyable={classData.tenantId}>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                  {classData.tenantId}
                </code>
              </InfoItem>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">模板（默认值）</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="Server URL" copyable={tpl?.serverUrl}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {tpl?.serverUrl || '-'}
              </code>
            </InfoItem>
            <InfoItem label="传输方式">
              <Badge variant="secondary">{MCP_TRANSPORT_LABELS[tpl?.transport ?? 0] || '-'}</Badge>
            </InfoItem>
            <InfoItem label="超时（秒）">
              <span className="text-sm">
                {tpl?.timeoutSeconds && tpl.timeoutSeconds > 0 ? `${tpl.timeoutSeconds}s` : '默认'}
              </span>
            </InfoItem>
            <InfoItem label="TLS 校验">
              {tpl?.skipTlsVerify ? (
                <Badge variant="destructive" className="text-[10px]">
                  跳过校验
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">
                  默认校验
                </Badge>
              )}
            </InfoItem>
          </div>

          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Headers
            </span>
            {!tpl?.headers || Object.keys(tpl.headers).length === 0 ? (
              <p className="text-xs text-muted-foreground">暂无 header</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-1/3">名称</th>
                      <th className="px-3 py-2 text-left font-medium">值</th>
                      <th className="px-3 py-2 text-left font-medium w-24">可配置</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {Object.entries(tpl.headers).map(([k, v]) => {
                      const isParam = headerParamKeys.has(k);
                      return (
                        <tr key={k}>
                          <td className="px-3 py-2 font-mono text-xs">{k}</td>
                          <td className="px-3 py-2 font-mono text-xs break-all">{v || '-'}</td>
                          <td className="px-3 py-2">
                            {isParam ? (
                              <Badge variant="default" className="text-[10px]">
                                参数
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">固定</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">参数定义</CardTitle>
        </CardHeader>
        <CardContent>
          {!classData.params || classData.params.length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无可配置参数</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">名称</th>
                    <th className="px-3 py-2 text-left font-medium">类型</th>
                    <th className="px-3 py-2 text-left font-medium">Target</th>
                    <th className="px-3 py-2 text-left font-medium">默认值</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {classData.params.map((p: Param, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-xs">{p.name}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">
                          {p.type || 'string'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs break-all">{p.target || '-'}</td>
                      <td className="px-3 py-2 font-mono text-xs break-all">
                        {p.paramValue?.case === 'value' ? p.paramValue.value : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InstancesTab({ classId }: { classId: string }) {
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const { data, isLoading } = useQuery({
    queryKey: ['mcps', 'by-class', classId, page, pageSize, searchKeyword],
    queryFn: () =>
      mcpApi.listMCPs(
        create(ListRequestSchema, {
          page,
          pageSize,
          filter: { class_id: classId },
          fuzzy: searchKeyword.trim() ? { name: searchKeyword.trim() } : {},
        })
      ),
  });

  const instances = useMemo(() => (data?.data || []).filter(Boolean), [data]);

  const keyword = searchKeyword.trim();
  const totalCount = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-base">实例列表</CardTitle>
        <Input
          placeholder="按名称搜索..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-52"
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : instances.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {keyword ? '没有找到匹配的实例' : '该 MCP 模版暂无实例'}
            </p>
          </div>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">名称</th>
                  <th className="px-4 py-2.5 text-left font-medium">Server URL</th>
                  <th className="px-4 py-2.5 text-left font-medium">创建时间</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {instances.map((m: MCP) => (
                  <tr
                    key={m.id}
                    className="hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/plugins/mcps/instances/${m.id}`)}
                  >
                    <td className="px-4 py-2.5 font-medium">
                      <div className="text-primary hover:underline">{m.name}</div>
                      <span className="text-xs text-muted-foreground/60">ID: {m.id}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-[280px]">
                      {m.serverUrl || '-'}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      {formatTimestamp(m.createdAt)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="bg-muted/30 px-4 py-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        共 {totalCount} 条，第 {page} / {totalPages} 页
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-muted-foreground">每页</span>
                          <select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setPage(1);
                            }}
                            className="h-7 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="text-sm text-muted-foreground">条</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 19l-7-7 7-7"
                              />
                            </svg>
                          </Button>
                          <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-sm">
                            <span className="font-medium">{page}</span>
                            <span className="text-muted-foreground">/</span>
                            <span className="text-muted-foreground">{totalPages}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MCPClassDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: classData, isLoading } = useQuery({
    queryKey: ['mcp-class', id],
    queryFn: () => mcpClassApi.getMCPClass(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => mcpClassApi.deleteMCPClass(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-classes'] });
      navigate('/plugins/mcps?tab=class');
    },
  });

  const tabs = [
    { id: 'overview', label: '概览' },
    { id: 'instances', label: '实例' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-lg font-semibold mb-2">MCP 模版不存在</h3>
            <p className="text-muted-foreground mb-4">该 MCP 模版可能已被删除或不存在</p>
            <Button variant="outline" onClick={() => navigate('/plugins/mcps?tab=class')}>
              返回列表
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/mcps?tab=class')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{classData.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {classData.description || '暂无描述'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/classes/${id}/instances/new`)}
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            创建实例
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/plugins/mcps/classes/${id}/edit`)}
          >
            <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
            编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="mr-1.5 h-3.5 w-3.5" />
            删除
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={(tid) => setActiveTab(tid as TabId)} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' && <OverviewTab classData={classData} />}
        {activeTab === 'instances' && <InstancesTab classId={classData.id} />}
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除 MCP 模版「{classData.name}」吗？关联实例不会被自动删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
